import { sendDeleteConfirmation } from '~/features/utils';
import { getPendingRequestConflict } from '~/features/utils/pendingRequestConflict';
import {
  ALERT_VIEW_STATE,
  CONFIRMATION_VIEW_STATE,
  PEER_REFERENCE_STATE,
} from '~/features/xstateSystem';
import {
  Events,
  GetReferenceEvent,
  GetReferencesEvent,
  OnSendDeleteConfirmation,
  OnSendRequest,
  OnSendResendConfirmation,
  OnSetProviderIdEvent,
  OnSubmitError,
  PDFResponseEvent,
} from './events';
import { Context, Scope } from './types';

export const handleModal = (scope: Scope): Partial<Context> => {
  return {
    modal: !scope.context.modal,
  };
};

export const loadingTrue = (): Partial<Context> => {
  return {
    loading: true,
  };
};

export const loadingFalse = (): Partial<Context> => {
  return {
    loading: false,
  };
};

// [MCV-1814] Remember who the request went to, so a "pending request already
// exists" answer can name the address.
export const setRequestEmail = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnSendRequest;
  return { requestEmail: event.values.emailAddress };
};

// [MCV-1814] Keep the request dialog open and remember the pending request the
// 409 pointed at.
export const setPendingRef = (scope: Scope): Partial<Context> => {
  const conflict = getPendingRequestConflict((scope.event as { error?: unknown }).error);
  return {
    pendingRef: conflict
      ? { id: conflict.existingId, email: scope.context.requestEmail ?? '' }
      : null,
  };
};

export const clearPendingRef = (): Partial<Context> => ({ pendingRef: null });

export const setProviderId = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnSetProviderIdEvent;
  return {
    providerId: event.payload.providerId,
  };
};

export const setPeerReference = (scope: Scope): Partial<Context> => {
  const { output } = scope.event as GetReferenceEvent;
  const peerReference = output;

  return {
    peerRefs: [...scope.context.peerRefs, peerReference],
  };
};

export const setPeerReferences = (scope: Scope): Partial<Context> => {
  const { output } = scope.event as GetReferencesEvent;
  const peerReferences = output;
  return {
    peerRefs: peerReferences,
  };
};

// TODO: MCV--447-Breakout loadPDF Action to be reuseable
export const loadPdf = (scope: Scope) => {
  const { output } = scope.event as PDFResponseEvent;
  const blob = new Blob([output], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
};

export const submitError = (scope: Scope) => {
  const { payload } = scope.event as OnSubmitError;
  console.log('Submitting form Error! ', payload.err);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- system shape is loose across actor trees
export const showRequestResentAlert = (args: { system: any }) => {
  args.system.get(ALERT_VIEW_STATE)?.send({
    type: 'ON_SHOW_ALERT',
    payload: { text: 'Request resent', type: 'success', timeout: 4000 },
  });
};

// MCV-1194: surface a confirmation dialog before firing the real ON_DELETE_REFERENCE.
// Delete-without-confirm previously caused users to wipe out a peer-reference request
// they didn't mean to remove — the recipient had already been emailed at request time,
// so the deletion felt final even though there was no undo or "are you sure" step.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- system shape is loose across actor trees
export const openDeleteConfirmation = (args: { event: Events; system: any; context?: Context }) => {
  const event = args.event as OnSendDeleteConfirmation;
  const onConfirm = (id: number) => {
    args.system.get(PEER_REFERENCE_STATE).send({ type: 'ON_DELETE_REFERENCE', payload: { id } });
  };
  // [MCV-1872] A request still waiting on its reference is "revoked" in the
  // grid (same wording as Team and Provider Associations). The API hard-deletes
  // it, so the emailed link stops working. A received reference keeps the
  // generic delete confirmation.
  const target = args.context?.peerRefs.find((ref) => ref.id === event.payload.id);
  if (target && !target.referenceDate) {
    args.system.get(CONFIRMATION_VIEW_STATE).send({
      type: 'ON_SHOW_CONFIRMATION',
      payload: {
        title: 'Revoke Request',
        message:
          'Are you sure you want to revoke this reference request? The link already sent will stop working.',
        confirmLabel: 'Revoke',
        onConfirm: () => onConfirm(event.payload.id),
      },
    });
    return;
  }
  args.system
    .get(CONFIRMATION_VIEW_STATE)
    .send(sendDeleteConfirmation(() => onConfirm(event.payload.id)));
};

// [MCV-1838] Resending is not dangerous, so the confirmation is neutral rather
// than the destructive default. Confirm fires ON_RESEND_REFERENCE.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- system shape is loose across actor trees
export const openResendConfirmation = (args: { event: Events; system: any }) => {
  const { id, email } = (args.event as OnSendResendConfirmation).payload;
  args.system.get(CONFIRMATION_VIEW_STATE).send({
    type: 'ON_SHOW_CONFIRMATION',
    payload: {
      title: 'Resend Request',
      message: `Send the reference request to ${email} again? The link in the email stays the same.`,
      confirmLabel: 'Resend',
      tone: 'neutral',
      onConfirm: () => {
        args.system
          .get(PEER_REFERENCE_STATE)
          .send({ type: 'ON_RESEND_REFERENCE', payload: { id } });
      },
    },
  });
};
